import React from 'react';
import { ShieldCheck, Rocket, TrendingUp, Target, Sparkles } from 'lucide-react';
import { content } from '../data/content';

const Value: React.FC = () => {
  const { value } = content;

  const getIcon = (index: number) => {
    switch (index) {
      case 0: return <ShieldCheck className="w-12 h-12" />;
      case 1: return <Rocket className="w-12 h-12" />;
      case 2: return <TrendingUp className="w-12 h-12" />;
      case 3: return <Target className="w-12 h-12" />;
      default: return <ShieldCheck className="w-12 h-12" />;
    }
  };

  const cardStyles = [
    { 
      bg: "from-blue-600/20 via-cyan-500/10 to-blue-900/30", 
      border: "border-blue-400/30", 
      shadow: "shadow-blue-500/20", 
      iconColor: "text-cyan-300", 
      iconBg: "bg-blue-900/50",
      glow: "bg-blue-500"
    },
    { 
      bg: "from-purple-600/20 via-fuchsia-500/10 to-purple-900/30", 
      border: "border-purple-400/30", 
      shadow: "shadow-purple-500/20", 
      iconColor: "text-fuchsia-300", 
      iconBg: "bg-purple-900/50",
      glow: "bg-purple-500"
    },
    { 
      bg: "from-orange-600/20 via-amber-500/10 to-red-900/30", 
      border: "border-orange-400/30", 
      shadow: "shadow-orange-500/20", 
      iconColor: "text-amber-300", 
      iconBg: "bg-orange-900/50",
      glow: "bg-orange-500"
    },
    { 
      bg: "from-emerald-600/20 via-teal-500/10 to-green-900/30", 
      border: "border-emerald-400/30", 
      shadow: "shadow-emerald-500/20", 
      iconColor: "text-emerald-300", 
      iconBg: "bg-emerald-900/50",
      glow: "bg-emerald-500"
    },
  ];

  return (
    <section className="min-h-screen flex items-center justify-center py-24 bg-slate-900 relative overflow-hidden">
      {/* Galaxy Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-purple-900 via-slate-900 to-black"></div>
        <div className="absolute bottom-0 left-0 w-full h-full bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-blue-900/50 via-transparent to-transparent"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[150%] h-[150%] bg-[radial-gradient(circle,_var(--tw-gradient-stops))] from-pink-500/10 via-transparent to-transparent blur-3xl"></div>
        
        {/* Massive Stars */}
        {Array.from({ length: 500 }).map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full animate-twinkle"
            style={{
              width: Math.random() * 2 + 0.5 + 'px',
              height: Math.random() * 2 + 0.5 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
              animationDelay: Math.random() * 5 + 's',
              opacity: Math.random() * 0.8 + 0.2,
              boxShadow: Math.random() > 0.9 ? '0 0 4px 1px rgba(255, 255, 255, 0.8)' : 'none'
            }}
          ></div>
        ))}
      </div>

      {/* Light Glowing Effect */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[radial-gradient(circle,_var(--tw-gradient-stops))] from-blue-500/20 via-transparent to-transparent blur-[100px] pointer-events-none mix-blend-screen"></div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-20">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-900/30 border border-blue-500/30 text-blue-300 mb-6 backdrop-blur-sm">
            <Sparkles size={16} className="mr-2 text-blue-400" />
            <span className="text-sm font-semibold tracking-wider uppercase">Team Leader Value</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white via-blue-100 to-gray-300 mb-6 tracking-tight drop-shadow-lg">
            {value.title}
          </h2>
          <p className="text-xl text-blue-200/70 max-w-2xl mx-auto leading-relaxed">
            {value.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {value.items.map((item, index) => {
            const style = cardStyles[index % cardStyles.length];
            return (
              <div 
                key={index} 
                className={`group relative p-8 rounded-3xl bg-gradient-to-br ${style.bg} border ${style.border} backdrop-blur-xl hover:-translate-y-2 transition-all duration-500 ${style.shadow}`}
              >
                {/* Hover Glow Effect */}
                <div className={`absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-500 bg-gradient-to-r ${style.bg}`}></div>
                
                {/* Animated Border */}
                <div className="absolute inset-0 rounded-3xl overflow-hidden">
                  <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-white/50 to-transparent -translate-x-full group-hover:animate-shine"></div>
                </div>

                <div className={`w-16 h-16 rounded-2xl ${style.iconBg} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 border border-white/10 relative overflow-hidden`}>
                  <div className={`absolute inset-0 ${style.glow} opacity-20 blur-md`}></div>
                  <div className={`${style.iconColor} relative z-10`}>
                    {getIcon(index)}
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-gray-300 transition-colors">
                  {item.title}
                </h3>
                
                <p className="text-gray-300/80 leading-relaxed group-hover:text-white/90 transition-colors">
                  {item.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Value;
